import Phaser from 'phaser';
import { GAME_W, GAME_H, COIN_BOARD, COIN_CARD } from '../config.ts';
import { COINS, INK } from '../../assets/palette.ts';
import { coinsForPrice } from '../levels.ts';
import { Audio } from '../audio.ts';
import { makeCircleButton } from './button.ts';

const PAD_R = 60; // big tap target for little fingers
const SUPPLY_Y = GAME_H * 0.85;
const CARD = { x: COIN_CARD.x, y: COIN_CARD.y + 8, w: 290, h: 140 };

export interface PaymentOpts {
  /** Amount the card must add up to (price to pay, or change to give back). */
  target: number;
  /** 'change' shows a back-arrow + the coin the customer handed over. */
  mode: 'pay' | 'change';
  /** For 'change': the single coin value the customer paid with. */
  paidWith?: number;
  onComplete: () => void;
}

interface TrayCoin {
  img: Phaser.GameObjects.Image;
  value: number;
}

/**
 * The payment mini-game. The amount due is shown as a row of coins on the
 * price-board (no numerals); the child taps coin buttons to drop coins onto the
 * slot-card (and can drag any coin back off to remove it). When the card adds
 * up to the target, it completes — there is no wrong-and-stuck state.
 */
export class PaymentPanel {
  private scene: Phaser.Scene;
  private opts: PaymentOpts;
  private layer: Phaser.GameObjects.Container;
  private trayCoins: TrayCoin[] = [];
  private trayZone: Phaser.Geom.Rectangle;
  private done = false;
  private destroyed = false;

  constructor(scene: Phaser.Scene, opts: PaymentOpts) {
    this.scene = scene;
    this.opts = opts;
    this.layer = scene.add.container(0, 0).setDepth(50);
    this.trayZone = new Phaser.Geom.Rectangle(CARD.x - CARD.w / 2, CARD.y - CARD.h / 2, CARD.w, CARD.h);
    this.build();
  }

  private build(): void {
    const s = this.scene;
    const due = coinsForPrice(this.opts.target);

    // ── Amount due, shown as a row of coins on the price-board. ───────────────
    const boardGap = 64;
    const boardStart = COIN_BOARD.x - ((due.length - 1) * boardGap) / 2;
    due.forEach((v, i) => {
      this.layer.add(s.add.image(boardStart + i * boardGap, COIN_BOARD.y, `coin_${v}`).setDisplaySize(56, 56));
    });

    // Change mode: a back-arrow cue + the coin the customer handed over.
    if (this.opts.mode === 'change') {
      this.layer.add(
        s.add.image(COIN_BOARD.x - 168, COIN_BOARD.y, 'try_again').setDisplaySize(64, 64),
      );
      if (this.opts.paidWith) {
        this.layer.add(
          s.add.image(COIN_BOARD.x + 168, COIN_BOARD.y, `coin_${this.opts.paidWith}`).setDisplaySize(60, 60),
        );
      }
    }

    // ── Dotted slot hints on the card (how many coins, canonically). ──────────
    const g = s.add.graphics();
    const slotGap = Math.min(70, (CARD.w - 50) / Math.max(due.length, 1));
    const slotStart = CARD.x - (slotGap * (due.length - 1)) / 2;
    due.forEach((_v, i) => {
      g.lineStyle(4, Phaser.Display.Color.HexStringToColor(INK).color, 0.3);
      g.strokeCircle(slotStart + i * slotGap, CARD.y, 26);
    });
    this.layer.add(g);

    // ── Coin dispenser buttons along the bottom. ──────────────────────────────
    const padGap = 200;
    const padStart = GAME_W * 0.5 - ((COINS.length - 1) * padGap) / 2;
    COINS.forEach((coin, i) => {
      this.layer.add(this.makePad(padStart + i * padGap, SUPPLY_Y, coin.value));
    });

    // Coins on the card can be dragged off to remove them.
    s.input.on('dragstart', this.onDragStart, this);
    s.input.on('drag', this.onDrag, this);
    s.input.on('dragend', this.onDragEnd, this);
  }

  private makePad(x: number, y: number, value: number): Phaser.GameObjects.Container {
    const s = this.scene;
    const pad = s.add.container(x, y);
    const bg = s.add.circle(0, 0, PAD_R, 0xffffff).setStrokeStyle(6, Phaser.Display.Color.HexStringToColor(INK).color);
    const coin = s.add.image(0, 0, `coin_${value}`).setDisplaySize(92, 92);
    pad.add([bg, coin]);
    makeCircleButton(s, bg, PAD_R, () => {
      this.scene.tweens.add({ targets: pad, scale: 0.9, duration: 80, yoyo: true });
      this.dispense(value, x, y);
    });
    return pad;
  }

  /** Spawn a coin and send it onto the card. */
  private dispense(value: number, fromX: number, fromY: number): void {
    if (this.done || this.destroyed) return;
    const img = this.scene.add.image(fromX, fromY, `coin_${value}`).setDisplaySize(70, 70).setDepth(60);
    this.layer.add(img);
    const tc: TrayCoin = { img, value };
    this.trayCoins.push(tc);
    this.layoutTray();
    const slot = this.slotPos(this.trayCoins.length - 1);
    this.scene.tweens.add({
      targets: img,
      x: slot.x,
      y: slot.y,
      duration: 300,
      ease: 'Cubic.Out',
      onComplete: () => {
        if (this.destroyed || !img.active) return;
        Audio.coin();
        this.makeTrayCoinDraggable(tc);
        this.afterChange();
      },
    });
  }

  private slotPos(i: number, total = this.trayCoins.length): { x: number; y: number } {
    const gap = Math.min(64, (CARD.w - 40) / Math.max(total, 1));
    const startX = CARD.x - (gap * (total - 1)) / 2;
    return { x: startX + i * gap, y: CARD.y };
  }

  private layoutTray(): void {
    this.trayCoins.forEach((tc, i) => {
      const p = this.slotPos(i);
      this.scene.tweens.add({ targets: tc.img, x: p.x, y: p.y, duration: 150 });
    });
  }

  private makeTrayCoinDraggable(tc: TrayCoin): void {
    if (this.destroyed || !tc.img.active) return;
    tc.img.setInteractive({ draggable: true, useHandCursor: true });
    this.scene.input.setDraggable(tc.img);
    tc.img.setData('trayCoin', tc);
  }

  private onDragStart(_p: Phaser.Input.Pointer, obj: Phaser.GameObjects.Image): void {
    obj.setDepth(80);
    this.scene.tweens.add({ targets: obj, scale: obj.scale * 1.12, duration: 100 });
  }

  private onDrag(_p: Phaser.Input.Pointer, obj: Phaser.GameObjects.Image, dragX: number, dragY: number): void {
    obj.x = dragX;
    obj.y = dragY;
  }

  private onDragEnd(_p: Phaser.Input.Pointer, obj: Phaser.GameObjects.Image): void {
    const tc = obj.getData('trayCoin') as TrayCoin | undefined;
    if (!tc) return;
    const inside = Phaser.Geom.Rectangle.Contains(this.trayZone, obj.x, obj.y);
    if (inside) {
      this.layoutTray();
      this.scene.tweens.add({ targets: obj, scale: 70 / obj.width, duration: 120 });
    } else {
      // Dragged off => remove this coin (a forgiving "take it back").
      this.trayCoins = this.trayCoins.filter((t) => t !== tc);
      this.scene.tweens.add({
        targets: obj,
        alpha: 0,
        scale: 0,
        duration: 200,
        onComplete: () => obj.destroy(),
      });
      this.layoutTray();
      this.afterChange();
    }
  }

  private total(): number {
    return this.trayCoins.reduce((sum, c) => sum + c.value, 0);
  }

  // ── Scriptable hooks (used by the automated playability check) ──────────────
  get targetValue(): number {
    return this.opts.target;
  }
  get currentTotal(): number {
    return this.total();
  }
  addValue(value: number): void {
    this.dispense(value, GAME_W * 0.5, SUPPLY_Y);
  }

  private afterChange(): void {
    if (this.done || this.destroyed) return;
    const total = this.total();
    if (total === this.opts.target) {
      this.done = true;
      Audio.speak('Perfect!', { pitch: 1.3 });
      this.scene.time.delayedCall(250, () => this.opts.onComplete());
    } else if (total > this.opts.target) {
      Audio.wobble();
      this.scene.cameras.main.shake(120, 0.002);
    } else {
      Audio.speakNumber(total);
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.scene.input.off('dragstart', this.onDragStart, this);
    this.scene.input.off('drag', this.onDrag, this);
    this.scene.input.off('dragend', this.onDragEnd, this);
    for (const tc of this.trayCoins) this.scene.tweens.killTweensOf(tc.img);
    this.layer.destroy();
  }
}
